import styled from 'academy-common-lib/utils/Styling';
import { View } from 'foundations-components/components/View';
import { useCallback, useEffect, useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_WIDTH = 480;
const MIN_SIZE = 20;

// @bend-expect trellis-tokenized
const ResizableDiv = styled.div.withConfig({
  displayName: "ResizableContainer__ResizableDiv",
  componentId: "sc-14j72yp-0"
})(["min-width:", "px;min-height:", "px;z-index:99;border-radius:10px;"], MIN_WIDTH, MIN_WIDTH * 0.5625 + 20);
// @bend-expect trellis-tokenized
const ResizerTopLeft = styled.div.withConfig({
  displayName: "ResizableContainer__ResizerTopLeft",
  componentId: "sc-14j72yp-1"
})(["cursor:nwse-resize;"]);
// @bend-expect trellis-tokenized
const ResizerTopRight = styled.div.withConfig({
  displayName: "ResizableContainer__ResizerTopRight",
  componentId: "sc-14j72yp-2"
})(["cursor:nesw-resize;"]);
// @bend-expect trellis-tokenized
const ResizerBottomLeft = styled.div.withConfig({
  displayName: "ResizableContainer__ResizerBottomLeft",
  componentId: "sc-14j72yp-3"
})(["cursor:nesw-resize;"]);
// @bend-expect trellis-tokenized
const ResizerBottomRight = styled.div.withConfig({
  displayName: "ResizableContainer__ResizerBottomRight",
  componentId: "sc-14j72yp-4"
})(["cursor:nwse-resize;"]);
let originalMouseX = 0;
let originalMouseY = 0;
let originalWidth = 0;
let originalHeight = 0;
let originalX = 0;
const getOriginalDimensions = element => {
  if (!element) return;
  originalWidth = parseFloat(getComputedStyle(element, null).getPropertyValue('width'));
  originalHeight = parseFloat(getComputedStyle(element, null).getPropertyValue('height'));
};
const ResizableContainer = ({
  children,
  onResizing
}) => {
  const element = useRef(null);
  const resizerTopLeft = useRef(null);
  const resizerTopRight = useRef(null);
  const resizerBottomLeft = useRef(null);
  const resizerBottomRight = useRef(null);
  const currentResizerRef = useRef(null);
  const mouseMoveHandler = useCallback(e => {
    if (element.current && element.current.style && originalX) {
      var _currentResizerRef$cu;
      const currentResizerElement = (_currentResizerRef$cu = currentResizerRef.current) === null || _currentResizerRef$cu === void 0 ? void 0 : _currentResizerRef$cu.target;
      onResizing(true);
      if (currentResizerElement.id === 'resizable-bottom-right') {
        const width = originalWidth + (e.pageX - originalMouseX);
        const height = originalHeight + (e.pageY - originalMouseY);
        if (width > MIN_SIZE) {
          element.current.style.width = `${width}px`;
        }
        if (height > MIN_SIZE) {
          element.current.style.height = `${width * 0.5625 + 20}px`;
        }
      } else if (currentResizerElement.id === 'resizable-bottom-left') {
        const width = originalWidth - (e.pageX - originalMouseX);
        const height = originalHeight - (e.pageY - originalMouseY);
        if (height > MIN_SIZE) {
          element.current.style.height = `${width * 0.5625 + 20}px`;
        }
        if (width > MIN_SIZE) {
          element.current.style.width = `${width}px`;
          element.current.style.left = `${originalX + (e.pageX - originalMouseX)}px`;
        }
      } else if (currentResizerElement.id === 'resizable-top-right') {
        const width = originalWidth + (e.pageX - originalMouseX);
        const height = originalHeight - (e.pageY - originalMouseY);
        if (width > MIN_SIZE) {
          element.current.style.width = `${width}px`;
        }
        if (height > MIN_SIZE) {
          element.current.style.height = `${width * 0.5625 + 20}px`;
          element.current.style.top = `${e.pageY - originalMouseY}px`;
        }
      } else {
        const width = originalWidth - (e.pageX - originalMouseX);
        const height = originalHeight - (e.pageY - originalMouseY);
        if (width > MIN_SIZE) {
          element.current.style.width = `${width}px`;
          element.current.style.left = `${originalX + (e.pageX - originalMouseX)}px`;
        }
        if (height > MIN_SIZE) {
          element.current.style.height = `${width * 0.5625 + 20}px`;
          element.current.style.top = `${e.pageY - originalMouseY}px`;
        }
      }
    }
  }, [onResizing]);
  const stopResizeWrapper = useCallback(() => {
    onResizing(false);
    window.removeEventListener('mousemove', mouseMoveHandler);
  }, [mouseMoveHandler, onResizing]);
  const onMouseDown = useCallback(currentResizer => {
    var _element$current;
    if (currentResizer.stopPropagation) currentResizer.stopPropagation();
    if (currentResizer.preventDefault) currentResizer.preventDefault();
    currentResizerRef.current = currentResizer;
    getOriginalDimensions(element.current);
    originalX = (_element$current = element.current) === null || _element$current === void 0 ? void 0 : _element$current.getBoundingClientRect().left;
    originalMouseX = currentResizer.pageX;
    originalMouseY = currentResizer.pageY;
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', stopResizeWrapper);
  }, [mouseMoveHandler, stopResizeWrapper]);
  const createResizableContainer = useCallback(() => {
    var _resizerTopLeft$curre, _resizerTopRight$curr, _resizerBottomLeft$cu, _resizerBottomRight$c;
    (_resizerTopLeft$curre = resizerTopLeft.current) === null || _resizerTopLeft$curre === void 0 || _resizerTopLeft$curre.addEventListener('mousedown', onMouseDown);
    (_resizerTopRight$curr = resizerTopRight.current) === null || _resizerTopRight$curr === void 0 || _resizerTopRight$curr.addEventListener('mousedown', onMouseDown);
    (_resizerBottomLeft$cu = resizerBottomLeft.current) === null || _resizerBottomLeft$cu === void 0 || _resizerBottomLeft$cu.addEventListener('mousedown', onMouseDown);
    (_resizerBottomRight$c = resizerBottomRight.current) === null || _resizerBottomRight$c === void 0 || _resizerBottomRight$c.addEventListener('mousedown', onMouseDown);
  }, [onMouseDown]);
  const cleanup = useCallback(() => {
    var _resizerTopRight$curr2, _resizerBottomLeft$cu2, _resizerBottomRight$c2, _resizerTopLeft$curre2;
    window.removeEventListener('mouseup', stopResizeWrapper);
    window.removeEventListener('mousemove', mouseMoveHandler);
    (_resizerTopRight$curr2 = resizerTopRight.current) === null || _resizerTopRight$curr2 === void 0 || _resizerTopRight$curr2.removeEventListener('mousedown', onMouseDown);
    (_resizerBottomLeft$cu2 = resizerBottomLeft.current) === null || _resizerBottomLeft$cu2 === void 0 || _resizerBottomLeft$cu2.removeEventListener('mousedown', onMouseDown);
    (_resizerBottomRight$c2 = resizerBottomRight.current) === null || _resizerBottomRight$c2 === void 0 || _resizerBottomRight$c2.removeEventListener('mousedown', onMouseDown);
    (_resizerTopLeft$curre2 = resizerTopLeft.current) === null || _resizerTopLeft$curre2 === void 0 || _resizerTopLeft$curre2.removeEventListener('mousedown', onMouseDown);
  }, [mouseMoveHandler, onMouseDown, stopResizeWrapper]);
  useEffect(() => {
    createResizableContainer();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/_jsxs(ResizableDiv, {
    id: "resizer-div",
    ref: element,
    children: [/*#__PURE__*/_jsx(ResizerTopLeft, {
      ref: resizerTopLeft,
      children: /*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-transparent-default",
        blockSize: "16px",
        id: "resizable-top-left",
        inlineSize: "16px",
        insetBlockStart: "-4px",
        insetInlineStart: "-4px",
        position: "absolute"
      })
    }), /*#__PURE__*/_jsx(ResizerTopRight, {
      ref: resizerTopRight,
      children: /*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-transparent-default",
        blockSize: "16px",
        id: "resizable-top-right",
        inlineSize: "16px",
        insetBlockStart: "-4px",
        insetInlineEnd: "-4px",
        position: "absolute"
      })
    }), /*#__PURE__*/_jsx(ResizerBottomLeft, {
      ref: resizerBottomLeft,
      children: /*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-transparent-default",
        blockSize: "16px",
        id: "resizable-bottom-left",
        inlineSize: "16px",
        insetBlockEnd: "-4px",
        insetInlineStart: "-4px",
        position: "absolute"
      })
    }), /*#__PURE__*/_jsx(ResizerBottomRight, {
      ref: resizerBottomRight,
      children: /*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-transparent-default",
        blockSize: "16px",
        id: "resizable-bottom-right",
        inlineSize: "16px",
        insetBlockEnd: "-4px",
        insetInlineEnd: "-4px",
        position: "absolute"
      })
    }), children]
  });
};
export default ResizableContainer;